import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="data-exports--definition-form"
export default class extends Controller {
  static targets = [
    "sourceCheckbox", "availableList", "availableCount",
    "selectedBody", "selectedCount", "fieldSearch"
  ]
  static values = { catalog: Array, definition: Object }

  static SOURCE_LABELS = { attendance: "勤怠", performance: "作業", wage: "手当" }
  static AGG_METHODS = [["", ""], ["sum", "SUM"], ["count", "COUNT"], ["avg", "AVG"], ["max", "MAX"], ["min", "MIN"]]
  static SORT_DIRECTIONS = [["", ""], ["asc", "ASC"], ["desc", "DESC"]]

  connect() {
    this.columns = JSON.parse(JSON.stringify(this.definitionValue.columns || []))
    this.refreshAvailable()
  }

  selectedSources() {
    return this.sourceCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.dataset.source)
  }

  refreshAvailable() {
    const sources = this.selectedSources()
    this.available = this.catalogValue.filter((f) => f.source_types.some((s) => sources.includes(s)))
    this.columns = this.columns.filter((c) => this.available.some((f) => f.id === c.exportable_item_id))
    this.renderAvailable()
    this.renderSelected()
  }

  renderAvailable() {
    const q = this.hasFieldSearchTarget ? this.fieldSearchTarget.value.trim() : ""
    const selectedIds = new Set(this.columns.map((c) => c.exportable_item_id))
    const rows = this.available
      .filter((f) => !selectedIds.has(f.id))
      .filter((f) => !q || f.name.includes(q))

    this.availableCountTarget.textContent = `${this.available.length}項目`

    if (rows.length === 0) {
      this.availableListTarget.innerHTML = '<div class="empty">追加可能な項目はありません</div>'
      return
    }

    const sources = this.selectedSources()
    this.availableListTarget.innerHTML = rows
      .map((f) => {
        const badges = f.source_types
          .filter((s) => sources.includes(s))
          .map((s) => `<span class="badge ${s}">${this.constructor.SOURCE_LABELS[s]}</span>`)
          .join("")

        return `<div class="field-item">
          <div>
            <div class="field-name">${f.name}</div>
            <div class="badges">${badges}</div>
          </div>
          <button type="button" class="btn small primary"
                  data-action="click->data-exports--definition-form#add" data-id="${f.id}">追加</button>
        </div>`
      })
      .join("")
  }

  add(event) {
    this.addColumn(Number(event.currentTarget.dataset.id))
    this.renderAvailable()
    this.renderSelected()
  }

  addAll() {
    this.available.forEach((f) => this.addColumn(f.id))
    this.renderAvailable()
    this.renderSelected()
  }

  addColumn(id) {
    if (this.columns.some((c) => c.exportable_item_id === id)) return

    const f = this.catalogValue.find((x) => x.id === id)
    this.columns.push({
      exportable_item_id: f.id, name: f.name, value_type: f.value_type, display_name: f.name,
      grouped: false, aggregated: false, aggregate_method: "", sort_direction: "",
      sort_index: this.columns.length + 1
    })
  }

  clear() {
    if (!window.confirm("選択済み項目を全てクリアしますか？")) return
    this.columns = []
    this.renderAvailable()
    this.renderSelected()
  }

  autoSort() {
    this.columns.forEach((c, i) => { c.sort_index = i + 1 })
    this.renderSelected()
  }

  moveUp(event) { this.move(this.rowIndex(event), -1) }
  moveDown(event) { this.move(this.rowIndex(event), 1) }

  move(i, dir) {
    const ni = i + dir
    if (ni < 0 || ni >= this.columns.length) return
      ;[this.columns[i], this.columns[ni]] = [this.columns[ni], this.columns[i]]
    this.autoSort()
  }

  remove(event) {
    this.columns.splice(this.rowIndex(event), 1)
    this.autoSort()
    this.renderAvailable()
  }

  updateSortIndex(event) {
    const from = this.rowIndex(event)
    const target = Number(event.target.value)
    const max = this.columns.length

    if (!Number.isInteger(target) || target < 1 || target > max) {
      window.alert(`順番は 1〜${max} の範囲で入力してください。`)
      this.renderSelected()
      return
    }

    const [moved] = this.columns.splice(from, 1)
    this.columns.splice(target - 1, 0, moved)
    this.autoSort()
  }

  updateDisplayName(event) { this.columns[this.rowIndex(event)].display_name = event.target.value }
  toggleGroup(event) { this.columns[this.rowIndex(event)].grouped = event.target.checked }
  toggleAggregate(event) { this.columns[this.rowIndex(event)].aggregated = event.target.checked }
  updateMethod(event) { this.columns[this.rowIndex(event)].aggregate_method = event.target.value }
  updateSort(event) { this.columns[this.rowIndex(event)].sort_direction = event.target.value }

  rowIndex(event) {
    return Number(event.target.closest("tr").dataset.index)
  }

  options(pairs, selected) {
    return pairs
      .map(([value, label]) => `<option value="${value}" ${selected === value ? "selected" : ""}>${label}</option>`)
      .join("")
  }

  renderSelected() {
    this.selectedCountTarget.textContent = `${this.columns.length}項目`

    if (this.columns.length === 0) {
      this.selectedBodyTarget.innerHTML =
        '<tr><td colspan="8" class="empty">出力する項目を左側から追加してください</td></tr>'
      return
    }

    this.selectedBodyTarget.innerHTML = this.columns
      .map((c, i) => {
        const f = this.catalogValue.find((x) => x.id === c.exportable_item_id)
        const aggDisabled = f && f.aggregatable ? "" : "disabled"
        const methodOptions = this.options(this.constructor.AGG_METHODS, c.aggregate_method)
        const sortOptions = this.options(this.constructor.SORT_DIRECTIONS, c.sort_direction)
        const name = (attr) => `items[${i}][${attr}]`

        return `<tr data-index="${i}">
          <td class="cell-order">
            <input type="hidden" name="${name("exportable_item_id")}" value="${c.exportable_item_id}">
            <input class="order-input" type="number" min="1" max="${this.columns.length}" name="${name("sort_index")}" value="${c.sort_index}"
                   data-action="change->data-exports--definition-form#updateSortIndex">
          </td>
          <td class="cell-field" title="${c.name}"><b>${c.name}</b></td>
          <td class="cell-caption">
            <input type="text" maxlength="16" name="${name("display_name")}" value="${c.display_name}"
                   data-action="change->data-exports--definition-form#updateDisplayName">
          </td>
          <td class="cell-group">
            <input type="hidden" name="${name("grouped")}" value="0">
            <input type="checkbox" name="${name("grouped")}" value="1" ${c.grouped ? "checked" : ""}
                   data-action="change->data-exports--definition-form#toggleGroup">
          </td>
          <td class="cell-agg">
            <input type="hidden" name="${name("aggregated")}" value="0">
            <input type="checkbox" name="${name("aggregated")}" value="1" ${c.aggregated ? "checked" : ""} ${aggDisabled}
                   data-action="change->data-exports--definition-form#toggleAggregate">
          </td>
          <td class="cell-method">
            <select name="${name("aggregate_method")}" ${aggDisabled} data-action="change->data-exports--definition-form#updateMethod">${methodOptions}</select>
          </td>
          <td class="cell-sort">
            <select name="${name("sort_direction")}" data-action="change->data-exports--definition-form#updateSort">${sortOptions}</select>
          </td>
          <td class="cell-ops btns">
            <button type="button" class="btn small" data-action="click->data-exports--definition-form#moveUp">↑</button>
            <button type="button" class="btn small" data-action="click->data-exports--definition-form#moveDown">↓</button>
            <button type="button" class="btn small danger" data-action="click->data-exports--definition-form#remove">×</button>
          </td>
        </tr>`
      })
      .join("")
  }
}
